/**
 * In-stay rental booking: payment frequency rules and the price calculation.
 *
 * The price is worked out here from the item itself (`calculateRentalQuote`):
 * the item's `price` is per hour / day / month / stay and `security_deposit` is
 * added once. There is no calculation endpoint. A booking ends `duration`
 * units after it starts, so 3 days from Oct 1 end on Oct 4.
 *
 * Order — POST /{room_uuid}/in-stay-rental/order sends the booking fields:
 *   in_stay_rental_item_id  item id
 *   payment_frequency       "hourly" | "daily" | "monthly" | "per_stay"
 *   from_date               "YYYY-MM-DD"  (hourly, daily, monthly)
 *   from_time               "HH:mm"       (hourly)
 *   duration                integer >= 1  (hours / days / months; not sent for per_stay)
 * plus the calculated to_date / to_time (hourly), subtotal, security_deposit
 * and total.
 */

import { getLocalDateString } from "./date";

const FREQUENCIES = {
  hourly: {
    label: "Hourly",
    durationBased: true,
    withStartTime: true,
    durationLabel: "Number of hours",
    rateSuffix: "/hour",
    unitOne: "{{value}} hour",
    unitOther: "{{value}} hours",
  },
  daily: {
    label: "Daily",
    durationBased: true,
    withStartTime: false,
    durationLabel: "Number of days",
    rateSuffix: "/day",
    unitOne: "{{value}} day",
    unitOther: "{{value}} days",
  },
  monthly: {
    label: "Monthly",
    durationBased: true,
    withStartTime: false,
    durationLabel: "Number of months",
    rateSuffix: "/month",
    unitOne: "{{value}} month",
    unitOther: "{{value}} months",
  },
  per_stay: {
    label: "Per Stay",
    durationBased: false,
    withStartTime: false,
  },
};

// Returns the configuration for a payment frequency, or null when the value is
// not one of the supported frequencies.
export const getRentalFrequency = (value) => {
  const key = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return FREQUENCIES[key] ? { key, ...FREQUENCIES[key] } : null;
};

// Backend field names that have an input in the booking form.
export const getRentalInputFields = (frequency) => {
  if (!frequency?.durationBased) return [];

  return frequency.withStartTime
    ? ["from_date", "from_time", "duration"]
    : ["from_date", "duration"];
};

// A duration is a whole number of hours / days / months greater than zero.
export const parseRentalDuration = (value) => {
  const text = String(value ?? "").trim();

  if (!/^\d+$/.test(text)) return null;

  const duration = Number(text);

  return duration > 0 && Number.isSafeInteger(duration) ? duration : null;
};

const toLocalDate = (date, time) => {
  const [year, month, day] = String(date ?? "")
    .split("-")
    .map(Number);
  const [hours = 0, minutes = 0] = time
    ? String(time).split(":").map(Number)
    : [];
  const value = new Date(year, month - 1, day, hours, minutes);

  return Number.isNaN(value.getTime()) ? null : value;
};

export const isPastRentalStart = (date, time, now = new Date()) => {
  const start = toLocalDate(date, time);

  return Boolean(start) && start < now;
};

export const buildRentalBookingFields = ({
  itemId,
  frequency,
  startDate,
  startTime,
  duration,
}) => {
  const fields = {
    in_stay_rental_item_id: itemId,
    payment_frequency: frequency.key,
  };

  if (frequency.durationBased) {
    fields.from_date = startDate;

    if (frequency.withStartTime) {
      fields.from_time = startTime;
    }

    fields.duration = duration;
  }

  return fields;
};

export const buildRentalOrderFields = (bookingFields, quote) => {
  const fields = { ...bookingFields };

  if (quote.endDate) fields.to_date = quote.endDate;
  if (quote.endTime) fields.to_time = quote.endTime;

  fields.subtotal = quote.subtotal;
  fields.security_deposit = quote.deposit;
  fields.total = quote.total;

  return fields;
};

const toAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const amount = Number(value);

  return Number.isFinite(amount) ? amount : null;
};

// An item without a usable price cannot be quoted or booked.
export const hasRentalPrice = (price) => toAmount(price) !== null;

const MS_PER_HOUR = 60 * 60 * 1000;

const daysInMonth = (year, monthIndex) =>
  new Date(year, monthIndex + 1, 0).getDate();

// When a booking that starts at `date` / `time` ends. Hourly bookings keep the
// time of day, monthly ones stay on the same day of the month and fall back to
// its last day when that month is shorter (Jan 31 + 1 month = Feb 28).
const getRentalEnd = (frequency, date, time, duration) => {
  const start = toLocalDate(date, frequency.withStartTime ? time : null);

  if (!start) return null;

  let end;

  if (frequency.key === "hourly") {
    end = new Date(start.getTime() + duration * MS_PER_HOUR);
  } else if (frequency.key === "daily") {
    end = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + duration
    );
  } else {
    const months = start.getMonth() + duration;
    const year = start.getFullYear() + Math.floor(months / 12);
    const month = months % 12;

    end = new Date(
      year,
      month,
      Math.min(start.getDate(), daysInMonth(year, month))
    );
  }

  if (Number.isNaN(end.getTime())) return null;

  const pad = (value) => String(value).padStart(2, "0");

  return {
    date: getLocalDateString(end),
    time: `${pad(end.getHours())}:${pad(end.getMinutes())}`,
  };
};

const toCents = (amount) => Math.round(amount * 100);

/**
 * Prices a booking from the item's own price and security deposit and returns
 * the quote shown in the breakdown and carried into checkout. Returns null when
 * the item has no usable price or the booking has no valid duration or dates,
 * so an incomplete quote can never be confirmed.
 *
 * `fields` are the booking fields from `buildRentalBookingFields`. Amounts are
 * summed in cents so 3 × 33.33 is 99.99 and not 99.99000000000001.
 */
export const calculateRentalQuote = ({
  frequency,
  fields,
  unitPrice,
  deposit,
}) => {
  const price = toAmount(unitPrice);
  const securityDeposit = toAmount(deposit) ?? 0;

  if (price === null || price < 0 || securityDeposit < 0) return null;

  const { durationBased, withStartTime } = frequency;
  const duration = durationBased ? parseRentalDuration(fields.duration) : null;

  if (durationBased && !duration) return null;

  const end = durationBased
    ? getRentalEnd(frequency, fields.from_date, fields.from_time, duration)
    : null;

  if (durationBased && !end) return null;

  const subtotal = toCents(price) * (duration ?? 1);
  const depositAmount = toCents(securityDeposit);

  return {
    frequency: frequency.key,
    unitPrice: price,
    duration,
    startDate: durationBased ? fields.from_date : null,
    startTime: withStartTime ? fields.from_time : null,
    endDate: end ? end.date : null,
    endTime: withStartTime && end ? end.time : null,
    subtotal: subtotal / 100,
    deposit: depositAmount / 100,
    total: (subtotal + depositAmount) / 100,
    currency: null,
  };
};

// Reads `{ message, errors | data: { field: [messages] } }` bodies.
export const getApiErrorDetails = (body) => {
  const source = body?.errors ?? body?.data;
  const fieldErrors = {};

  if (source && typeof source === "object" && !Array.isArray(source)) {
    Object.entries(source).forEach(([field, messages]) => {
      if (!Array.isArray(messages)) return;

      const message = messages.find(
        (entry) => typeof entry === "string" && entry.trim()
      );

      if (message) fieldErrors[field] = message;
    });
  }

  return {
    message: typeof body?.message === "string" ? body.message : "",
    fieldErrors,
  };
};

// Only validation / business errors carry messages meant for guests; network,
// routing and server errors fall back to a generic message.
export const getRequestErrorDetails = (error) => {
  const status = error?.response?.status;

  if (!status || status >= 500 || status === 404 || status === 405) {
    return { message: "", fieldErrors: {}, retryable: !status || status >= 500 };
  }

  return { ...getApiErrorDetails(error.response.data), retryable: false };
};

// Message for the errors that have no input of their own to be shown under.
export const getUnassignedErrorMessage = (error, inputFields, fallback) => {
  if (!error) return "";

  const entries = Object.entries(error.fieldErrors ?? {});
  const unassigned = entries
    .filter(([field]) => !inputFields.includes(field))
    .map(([, message]) => message);

  if (unassigned.length) return unassigned.join(" ");
  if (entries.length) return "";

  return error.message || fallback;
};

export const formatRentalAmount = (value) => {
  const amount = toAmount(value);

  return amount === null ? "" : amount.toFixed(2);
};

export const formatRentalDuration = (t, frequency, duration) => {
  if (!frequency?.durationBased || !duration) return "";

  return t(duration === 1 ? frequency.unitOne : frequency.unitOther, {
    value: duration,
  });
};

export const formatRentalDateTime = (date, time) => {
  const value = date ? toLocalDate(date, time) : null;

  if (!value) return "";

  const dateText = value.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  if (!time) return dateText;

  const timeText = value.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return `${dateText}, ${timeText}`;
};
